/**
 * 표지 공문 '붙임서류' 목록 편집기 (납품계·착수계 공용, 2026-07-04).
 *   각 행 = 문서이름 + 제출부수. 자유 입력(추가/삭제/순서 이동). 마지막에 '끝.' 은 자동.
 */

export interface DocAttachment {
  name: string;
  copies: string;
  /**
   * 어느 장인지 가리키는 **바뀌지 않는 표식** (v2.179.0).
   * 이 줄을 빼면 **PDF 에서도 그 장이 빠진다.**
   *
   * ⛔이름으로 맞추지 않는 이유 — 이름은 자유 편집이라 「예정공정표」를 「공정표」로 고치는 순간
   *   그 장이 조용히 사라진다. 표식은 이름을 고쳐도 그대로 따라다닌다.
   * ⚠사람이 [+ 붙임서류 추가] 로 더한 줄에는 표식이 **없다** — 표지에 글자로만 나온다(종전 그대로).
   */
  key?: string;
  /** 이 붙임 항목에 새로 첨부한 PDF 파일(전송 시 문서 뒤에 병합). */
  file?: File | null;
  /** 서버에 이미 저장된 첨부 PDF id(수정 모드). */
  existingFileId?: number | null;
  /** 서버 저장 첨부의 파일명(표시용). */
  existingFileName?: string | null;
}

interface Props {
  items: DocAttachment[];
  onChange: (items: DocAttachment[]) => void;
  /** 기본 목록으로 되돌리기 */
  onReset?: () => void;
}

export function AttachmentListEditor({ items, onChange, onReset }: Props) {
  const update = (i: number, patch: Partial<DocAttachment>) =>
    onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { name: '', copies: '1부' }]);
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };
  const pickFile = (i: number, f: File | null) => {
    if (f && !/\.pdf$/i.test(f.name)) { alert('PDF 파일만 첨부할 수 있습니다.'); return; }
    // 새 파일 선택 = 기존 첨부 대체(existingFileId 해제)
    update(i, { file: f, existingFileId: null, existingFileName: f ? f.name : null });
  };
  const clearFile = (i: number) => update(i, { file: null, existingFileId: null, existingFileName: null });

  return (
    <div className="border border-slate-200 rounded p-2 bg-slate-50">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium text-slate-600">붙임서류 ({items.length})</span>
        {onReset && (
          <button type="button" onClick={onReset} className="text-xs text-blue-600 hover:underline">
            기본목록으로
          </button>
        )}
      </div>
      <div className="space-y-1">
        {items.map((it, i) => {
          const attachedName = it.file?.name ?? it.existingFileName ?? null;
          return (
            <div key={i} className="flex items-center gap-1 flex-wrap">
              <span className="text-xs text-slate-500 w-5 text-right">{i + 1})</span>
              <input
                type="text"
                value={it.name}
                onChange={(e) => update(i, { name: e.target.value })}
                placeholder="문서이름 (예: 사진대지)"
                className="ui-input ui-input-sm flex-1 min-w-[120px]"
              />
              <input
                type="text"
                value={it.copies}
                onChange={(e) => update(i, { copies: e.target.value })}
                placeholder="부수"
                className="ui-input ui-input-sm w-14 text-center"
              />
              {/* PDF 첨부 — 첨부 시 파일명 표시, 미첨부 시 📎 버튼 */}
              {attachedName ? (
                <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 rounded px-1.5 py-1 max-w-[130px]">
                  <span className="truncate" title={attachedName}>📎 {attachedName}</span>
                  <button type="button" onClick={() => clearFile(i)} title="첨부 제거" className="text-emerald-500 hover:text-rose-600">✕</button>
                </span>
              ) : (
                <label className="text-xs px-1.5 py-1 border border-slate-300 rounded cursor-pointer hover:bg-white text-slate-500" title="이 붙임의 PDF 첨부(문서 뒤에 병합)">
                  📎 PDF
                  <input type="file" accept="application/pdf,.pdf" className="hidden"
                    onChange={(e) => pickFile(i, e.target.files?.[0] ?? null)} />
                </label>
              )}
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                title="위로" className="px-1 text-slate-400 hover:text-slate-700 disabled:opacity-30">↑</button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1}
                title="아래로" className="px-1 text-slate-400 hover:text-slate-700 disabled:opacity-30">↓</button>
              <button type="button" onClick={() => remove(i)}
                title="삭제" className="px-1 text-rose-400 hover:text-rose-600">✕</button>
            </div>
          );
        })}
      </div>
      <button type="button" onClick={add}
        className="btn btn-secondary btn-sm mt-1.5">
        + 붙임서류 추가
      </button>
      <p className="mt-1 text-[11px] text-slate-500">※ 붙임 항목에 PDF를 첨부하면 생성된 문서 맨 뒤에 순서대로 병합됩니다.</p>
    </div>
  );
}

/**
 * 납품계 기본 붙임 목록 — **줄마다 표식(key)이 PDF 의 한 장과 맞물린다** (v2.179.0).
 * 줄을 빼면 그 장이 PDF 에서도 빠진다. ⚠표식은 서버 `lib/delivery/doc-parts.ts` 와 같은 글자여야 한다.
 */
export const NAPUM_DEFAULT_ATTACHMENTS: DocAttachment[] = [
  { key: 'napum', name: '납품계', copies: '1부' },
  { key: 'geomsa', name: '납품검사원', copies: '1부' },
  { key: 'naeyeok', name: '납품내역서', copies: '1부' },
  { key: 'boan', name: '보안확약서', copies: '1부' },
  { key: 'bunap', name: '분할납품요구서', copies: '1부' },
];

/** 착수계 기본 붙임 목록 — 줄마다 표식이 PDF 한 장과 맞물린다 (v2.179.0). */
export const CHAKSU_DEFAULT_ATTACHMENTS: DocAttachment[] = [
  { key: 'chaksu', name: '착수계', copies: '1부' },
  { key: 'daeri', name: '사업책임자(현장대리인)계', copies: '1부' },
  { key: 'jaejik', name: '현장대리인 재직증명서', copies: '1부' },
  { key: 'jagyeok', name: '현장대리인 자격증사본', copies: '1부' },
  { key: 'gongjeong', name: '예정공정표', copies: '1부' },
  { key: 'naeyeok', name: '착수내역서', copies: '1부' },
  { key: 'boan_rep', name: '보안서약서 (업체대표)', copies: '1부' },
  { key: 'boan_daeri', name: '보안서약서 (현장대리인)', copies: '1부' },
  { key: 'bunap', name: '분할납품요구및통지서', copies: '1부' },
];

/**
 * 옛 자료에 표식을 되붙인다 — 2026-09-15 이전에 저장한 붙임 목록에는 표식이 없다.
 * **이름이 기본 목록과 똑같을 때만** 붙인다(이름을 고쳤으면 안 붙는다 = 그 장은 그대로 나온다).
 * ⚠이름으로 맞추는 것은 **여기 한 번뿐**이다 — 한 번 붙으면 그 뒤로는 표식만 따라다닌다.
 */
export function withKeys(saved: DocAttachment[], defaults: DocAttachment[]): DocAttachment[] {
  if (saved.some((it) => it.key)) return saved;          // 이미 표식이 있으면 그대로
  const byName = new Map(defaults.map((d) => [d.name.replace(/\s+/g, ''), d.key]));
  return saved.map((it) => {
    const k = byName.get(String(it.name ?? '').replace(/\s+/g, ''));
    return k ? { ...it, key: k } : it;
  });
}
